import { MicrodelPanel, Order, OrderItem, Panel } from "@app/provider/order";
import { isComboOrder, isPanoramaOrder } from "@app/utils";
import { TestType } from "@app/provider/testData/types";

enum PRICE_ESTIMATIONS {
  PRICE_249_349 = "$ 249.00 - $ 349.00",
  PRICE_249 = "$ 249.00",
  PRICE_349 = "$ 349.00",
  PRICE_449 = "$ 449.00",
  PRICE_99 = "$ 99.00",
}

export enum PanelNames {
  CARRIER_SCREENING_3_COMM_DMD = "CARRIER_SCREENING_3_COMM_DMD",
  CARRIER_SCREENING_3_COMM_CF = "CARRIER_SCREENING_3_COMM_CF",
  CARRIER_SCREENING_3_COMM_TSE = "CARRIER_SCREENING_3_COMM_TSE",
  CARRIER_SCREENING_3_COMM_SMA = "CARRIER_SCREENING_3_COMM_SMA",
  CARRIER_SCREENING_3_COMM_HCUSTOM = "CARRIER_SCREENING_3_COMM_HCUSTOM",
  CARRIER_SCREENING_3_COMM_HBASIC = "CARRIER_SCREENING_3_COMM_HBASIC",
  CARRIER_SCREENING_3_COMM_H3 = "CARRIER_SCREENING_3_COMM_H3",
  CARRIER_SCREENING_3_COMM_H4 = "CARRIER_SCREENING_3_COMM_H4",
  CARRIER_SCREENING_3_COMM_H14 = "CARRIER_SCREENING_3_COMM_H14",
  CARRIER_SCREENING_3_COMM_H22 = "CARRIER_SCREENING_3_COMM_H22",
  CARRIER_SCREENING_3_COMM_H27 = "CARRIER_SCREENING_3_COMM_H27",
  CARRIER_SCREENING_3_COMM_H28 = "CARRIER_SCREENING_3_COMM_H28",
  CARRIER_SCREENING_3_COMM_H106 = "CARRIER_SCREENING_3_COMM_H106",
  CARRIER_SCREENING_3_COMM_H112 = "CARRIER_SCREENING_3_COMM_H112",
  CARRIER_SCREENING_3_COMM_H254 = "CARRIER_SCREENING_3_COMM_H254",
  CARRIER_SCREENING_3_COMM_H274 = "CARRIER_SCREENING_3_COMM_H274",
  CARRIER_SCREENING_3_COMM_H275 = "CARRIER_SCREENING_3_COMM_H275",
  CARRIER_SCREENING_3_COMM_H421 = "CARRIER_SCREENING_3_COMM_H421",
  CARRIER_SCREENING_3_COMM_H445 = "CARRIER_SCREENING_3_COMM_H445",
}

export const panelNameToGenesMapper = (panelName: PanelNames): number => {
  const mapper = {
    [PanelNames.CARRIER_SCREENING_3_COMM_DMD]: 1,
    [PanelNames.CARRIER_SCREENING_3_COMM_CF]: 1,
    [PanelNames.CARRIER_SCREENING_3_COMM_TSE]: 1,
    [PanelNames.CARRIER_SCREENING_3_COMM_SMA]: 1,
    [PanelNames.CARRIER_SCREENING_3_COMM_HBASIC]: 4,
    [PanelNames.CARRIER_SCREENING_3_COMM_HCUSTOM]: 0,
    [PanelNames.CARRIER_SCREENING_3_COMM_H3]: 3,
    [PanelNames.CARRIER_SCREENING_3_COMM_H4]: 4,
    [PanelNames.CARRIER_SCREENING_3_COMM_H14]: 14,
    [PanelNames.CARRIER_SCREENING_3_COMM_H22]: 22,
    [PanelNames.CARRIER_SCREENING_3_COMM_H27]: 27,
    [PanelNames.CARRIER_SCREENING_3_COMM_H28]: 28,
    [PanelNames.CARRIER_SCREENING_3_COMM_H106]: 106,
    [PanelNames.CARRIER_SCREENING_3_COMM_H112]: 112,
    [PanelNames.CARRIER_SCREENING_3_COMM_H254]: 254,
    [PanelNames.CARRIER_SCREENING_3_COMM_H274]: 274,
    [PanelNames.CARRIER_SCREENING_3_COMM_H275]: 275,
    [PanelNames.CARRIER_SCREENING_3_COMM_H421]: 421,
    [PanelNames.CARRIER_SCREENING_3_COMM_H445]: 445,
  };

  return mapper[panelName];
};

export const getCommercialInsurancePriceEstimation = (
  order: Order | undefined
): string => {
  if (!order) {
    return PRICE_ESTIMATIONS.PRICE_249;
  }
  return getPriceEstimation(order);
};

const getComboPrice = (
  horizonPanel: Panel,
  microdelPanel: MicrodelPanel
): string => {
  const name = horizonPanel.name as keyof typeof PanelNames;
  const genesCount = panelNameToGenesMapper(PanelNames[name]);
  return genesCount >= 28 || microdelPanel === MicrodelPanel.FULL_PANEL
    ? PRICE_ESTIMATIONS.PRICE_449
    : PRICE_ESTIMATIONS.PRICE_349;
};

const getHorizonPanelWithMaxGenes = (horizonItem: OrderItem): Panel =>
  horizonItem.panels
    .filter((value) => Object.keys(PanelNames).includes(value.name))
    .sort(
      (a, b) =>
        panelNameToGenesMapper(PanelNames[b.name as keyof typeof PanelNames]) -
        panelNameToGenesMapper(PanelNames[a.name as keyof typeof PanelNames])
    )[0];

const getPanoramaPrice = (
  isCaPnsSupplemental?: boolean,
  microdelPanel?: MicrodelPanel | null
): string => {
  if (isCaPnsSupplemental) {
    switch (microdelPanel) {
      case MicrodelPanel.NONE:
        return PRICE_ESTIMATIONS.PRICE_99;
      case MicrodelPanel.DIGEORGE_ONLY:
        return PRICE_ESTIMATIONS.PRICE_249;
      default:
        return PRICE_ESTIMATIONS.PRICE_349;
    }
  }

  return microdelPanel === MicrodelPanel.FULL_PANEL
    ? PRICE_ESTIMATIONS.PRICE_349
    : PRICE_ESTIMATIONS.PRICE_249;
};

const getHorizonPrice = (horizonPanel: Panel): string => {
  const name = horizonPanel.name as keyof typeof PanelNames;

  if (name === PanelNames.CARRIER_SCREENING_3_COMM_HCUSTOM) {
    return PRICE_ESTIMATIONS.PRICE_249_349;
  }

  return panelNameToGenesMapper(PanelNames[name]) >= 28
    ? PRICE_ESTIMATIONS.PRICE_349
    : PRICE_ESTIMATIONS.PRICE_249;
};

export const getPriceEstimation = (order: Order): string => {
  const isCombo = isComboOrder(order);
  const isPanorama = isPanoramaOrder(order);

  const horizonItem = order.items.find(
    (item) => item.testType === TestType.HORIZON
  );
  const panoramaItem = order.items.find(
    (item) => item.testType === TestType.PANORAMA
  );
  const horizonPanel = horizonItem && getHorizonPanelWithMaxGenes(horizonItem);

  const microdelPanel = panoramaItem?.panels[0]?.microdelPanel;
  const isCaPnsSupplemental =
    panoramaItem?.product?.configuration?.caPnsSupplemental;

  if (isCombo && horizonPanel && microdelPanel) {
    return getComboPrice(horizonPanel, microdelPanel);
  }

  if (isPanorama) {
    return getPanoramaPrice(isCaPnsSupplemental, microdelPanel);
  }

  if (horizonPanel) {
    return getHorizonPrice(horizonPanel);
  }

  return PRICE_ESTIMATIONS.PRICE_249;
};
